// composables/useCollaborationStore.ts
import { reactive, ref, nextTick, computed } from 'vue';
import { useCollaborationApi } from '~/composables/useCollaborationApi';
import { useCommonApi } from '~/composables/useCommonApi';
import { useCodeStore } from '~/stores/useCodeStore';
import { isEmpty, isNotEmpty, nvl } from '~/composables/useCommon';
import { useTutorial } from '~/composables/useTutorial';
import { mapActivityListForCards } from '~/composables/domain/activity/mapActivityListForCards';
import { showConfirmDialog, showAlert } from '~/composables/useConfirmDialog';
import { appT } from '~/composables/useAppI18n';
import { translateResultStatusLabel } from '~/composables/task/useTaskFilterI18n';

/** store·UI 공통 — API/비교용 기간초과 라벨 (화면 표시는 t('task.home.overdue')) */
export const TASK_OVERDUE_DATE_LABEL = '기간초과';

export const TASK_MODAL_TAB = {
  PLAN: 'plan',
  ACTIVITY: 'activity',
  LOG: 'log',
} as const;

type TaskModalTabKey = (typeof TASK_MODAL_TAB)[keyof typeof TASK_MODAL_TAB];

const TASK_MODAL_TAB_KEYS = Object.values(TASK_MODAL_TAB);

const LEGACY_TASK_MODAL_TITLE_TO_TAB: Record<string, TaskModalTabKey> = {
  계획: TASK_MODAL_TAB.PLAN,
  활동: TASK_MODAL_TAB.ACTIVITY,
  로그: TASK_MODAL_TAB.LOG,
};

const resolveTaskModalTabKey = (item: { tabKey?: string; title?: string }): TaskModalTabKey | null => {
  if (item.tabKey && TASK_MODAL_TAB_KEYS.includes(item.tabKey as TaskModalTabKey)) {
    return item.tabKey as TaskModalTabKey;
  }
  return LEGACY_TASK_MODAL_TITLE_TO_TAB[item.title ?? ''] ?? null;
};
import { useExcelDownload } from '~/composables/useExcelDownload';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useKpiFormState } from '~/composables/performance/kpi/useKpiFormStore';

const { downloadExcel } = useExcelDownload();
const { fetchAtchFileList } = useAttachmentApi();

const $api = useCollaborationApi();
const $commonApi = useCommonApi();
const findHomeYear = ref('');
const findYear = ref('');

const searchBox = ref({
  visible: true,
  findDateVisible: true,
  findImportant: false,
  findFromDt: '',
  findToDt: '',
  dDayList: [], // JSON.parse('${codeUtil:getCodeJsons("414")}')
  searchMoreShow: false,
  coopShareUserList: [],
  maxMon: 12,
  findBoardId: '',
  findPlanNm: '',
  findDay: '',
  findAvtPlanStatus: '',
  findDtRange: [null, null] as (string | null)[],
  findResultStatusCd: [] as string[],
  findStatusCd: [] as string[],
  findCoopShareUserId: [] as string[]
});

const home = ref({
  subFilters: [],
  resultFilters: [],
  combinedFilters: [],
  boardSelectList: [],
  boardList: [],
  activityListLimit: 1,
  activityListRowsPerPage: '10', // '${searchVO.rows}'
  activityListRowsExist: true,
  activityListPage: 1, // activityList 조회용 page (초기값 1)
  boardSelectShow: false,
  findHomeActivityStartDt: '',
  findHomeActivityEndDt: '',
  homeActivityDtKeepYn: 'Y',
  findHomeActivitySearchDayGbn: '',
  findHomeActivitySearchKeyGbn: 'P',
  findHomeActivityKeyword: '',
  activityCollectYn: 'Y',
  activityCollectView: false,
  boardId: '',
  findFromDt: '',
  findToDt: '',
  findPlanNm: '',
  findDtRange: [null, null] as (string | null)[],
  planListPage: 1,
  planListRowsPerPage: 50,
  planListRowsExist: true,
  feedListPage: 1,
  feedListRowsPerPage: 100,
  feedListRowsExist: true,
  planList: [],
  activityList: [],
});

const board = ref({
  activityListLimit: 1,
  activityListRowsPerPage: '50',
  activityListRowsExist: true,
  activityUnreadCnt: 0,
  replyUnreadCnt: 0,
});

const plan = ref({
  planId: '',
  planNm: '',
  categoryId: '',
  categoryNm: '',
  startDt: '',
  endDt: '',
  predict: '',
  details: '',
  authCoop: '',
  authShare: '',
  activityCnt: '',
  ownerYn: 'Y',
  shareUserList: [],
  coopUserList: [],
  categoryList: [],
  activityList: [],
  activityUnreadCnt: 0,
  planType: '',
  ownerUserId: '',
});

const activity = ref([]);
const coopPlanCnt = ref('');
const sharePlanCnt = ref('');
const deliverPlanCnt = ref('');
const hasSearchedPlanNm = ref(false);
/** 현재 보드의 카테고리 수(업무명 검색 필터 적용 전, API categoryList 기준) */
const boardRawCategoryCount = ref(0);

const status = reactive({
  selectedSubFilterId: '',
  selectedResultStatusFilterId: '',
  planId: '',
  boardId: '',
  ownerYn: '',
  coopGbnCd: 'C',
  isPlanSortConfirmAsk: false,
  planActivityMonNm: '',
  planActivityMon: '',
  exceptYn: 'N',
  exceptReason: '',
  atchFileId: '',
  atchDownloadOnly: true,
  atchFileBtnObj: true,
  planEditorTabIndex: 1,
  visible: {
    attachModal: false,
    planEditor: false,
  },
});

const parts = ref({
  home: { visible: true },
  board: { visible: false },
  gantt: { visible: false },
  summary: { visible: false },
});

const settingData = ref({
  planSortConfirmYn: true,
});

const dialog = ref({
  setting: { visible: false },
  coopPlan: { visible: false },
  listAdd: { visible: false, mode: 'add' },
  /*planEditor: {
    visible: false,
    mode: 'add',
    planId: '',
  }*/
});

const homeDialog = ref({
  coopPlan: { visible: false },
  listAdd: { visible: false, mode: 'add' },
  planEditor: {
    plan: { visible: false },
    activity: { visible: false },
    log: { visible: false },
  },
});

const homePlan = ref({
  planId: '',
  planNm: '',
  categoryId: '',
  categoryNm: '',
  startDt: '',
  endDt: '',
  predict: '',
  details: '',
  authCoop: '',
  authShare: '',
  activityCnt: '',
  ownerYn: 'Y',
  shareUserList: [],
  coopUserList: [],
  categoryList: [],
  activityUnreadCnt: 0,
  activityList: [],
  avtLogList: [],
  activityListLimit: 1,
  activityListRowsPerPage: '10', // '${searchVO.rows}'
  activityListRowsExist: true,
});

const homeStatus = ref({
  boardId: '',
  planId: '',
});

const defaultParams = ref({
  boardNm: '.',
  planNm: '.',
  content: '.',
  statusCd: '.',
  resultStatusCd: '.',
});

const findStatusList = ref([]);
const sortCategoryIds = ref([]); // 업무이동시 정렬순서 재정렬할 카테고리IDS
const planList = ref([]);
const planList_home = ref([]);
const boardList = ref([]);
const boardListLoaded = ref(false); // 보드 목록 API 호출 완료 여부
const categoriesLoaded = ref(false); // 보드 카테고리(컬럼) 로딩 완료 여부
const boardList_home = ref([]);
const summaryBoardList = ref([]);
const moveBoardId = ref('');
const moveBoardList = ref([]);
const moveCategoryList = ref([]);
const mergeBoardId = ref('');
const mergeBoardList = ref([]);
const mergeCategoryList = ref([]);
const mergePlanList = ref([]);
const mailEventList = ref([]);
const categoryList = ref([]);
// 현재 categoryList가 로드된 boardId (보드 단위 캐시 무효화 판단용)
const categoryListBoardId = ref('');
const boardCategoryList = ref([]);
const resultStatusList = ref([]);
const avtPlanStatusList = ref([]);
const findAvtPlanStatus = ref('');
const statusList = ref([]);
const avtLogList = ref([]);
const myShareUserList = ref([]);
const coopPlanList = ref([]);
const coopPlanCategoryList = ref([]);
const userIdList = ref([]);
const shareUserIdList = ref([]);
const coopUserIdList = ref([]);
const categoryUserIdList = ref([]);
const categoryTakeoverUserIdList = ref([]);
const planTakeoverUserIdList = ref([]);
const activityLoadComplete = ref(false);
const readGbn = ref('03');
/** readGbn 01: 읽음 N·읽은 사람 목록 미표시 (NEW 배지는 유지) */
const isReadUserCntVisible = computed(() => String(readGbn.value ?? '').padStart(2, '0') !== '01');
const authGbn = ref('01');
const homeActivityYn = ref(true);
const summaryYn = ref(false);
const summarySelectSelectedList = ref([]);
const userList = ref([]);
/** 업무 전달·협업/공유 선택 드롭다운용 (isIncludeDept=N, 사용자만) */
const transferUserList = ref([]);
const coopShareUserList = ref([]);
const selectedBoard = ref(null);
const selectedTaskList = ref([]);
const isTaskModalOpen = ref(false);
const isInitialAllSelected = ref(true);
const triggerActivitySearch = ref(0); // 활동 검색 트리거
const triggerPlanSearchReset = ref(0); // 업무명 검색 버튼 클릭 시 사이드바 펼침 상태 초기화 트리거
const isNewClick = ref('N'); // NEW 클릭 여부
const collabTaskModalActivityReadOnly = ref(false);
const collabTaskModalFeedbackReadOnly = ref(false);
const collabTaskModalHideLog = ref(false);
/** 알림(ACTIVITY) 등 — 헤더 드래그로 우측 패널 이동(플로팅) 허용 */
const collabTaskModalAllowFloating = ref(false);
/** activityReadOnly일 때 수정/삭제 버튼 표시 (기본 false = 숨김) */
const collabTaskModalShowActivityEditDelete = ref(false);
/** title NEW 버튼 표시 — planData를 UiModal data로 넘길지 여부 (기본 false = 숨김) */
const collabTaskModalShowPlanReadNew = ref(false);
/** 업무 팝업 마지막 선택 탭 — 다른 업무 클릭 시에도 유지 */
const taskModalAccordionTab = ref<TaskModalTabKey>(TASK_MODAL_TAB.ACTIVITY);
const isTaskModalSwitching = ref(false);

const setTaskModalAccordionTab = (tabKey: string) => {
  if (TASK_MODAL_TAB_KEYS.includes(tabKey as TaskModalTabKey)) {
    taskModalAccordionTab.value = tabKey as TaskModalTabKey;
  }
};

const applyTaskModalAccordionToMenuItems = (items: any[], hideLog = false) => {
  let activeTab = taskModalAccordionTab.value;
  if (hideLog && activeTab === TASK_MODAL_TAB.LOG) {
    activeTab = TASK_MODAL_TAB.ACTIVITY;
    taskModalAccordionTab.value = activeTab;
  }

  let mapped = items.map(item => {
    const tabKey = resolveTaskModalTabKey(item);
    if (!item.isAccordion || !tabKey) {
      return item;
    }
    return {
      ...item,
      tabKey,
      title: item.title || appT(`task.home.taskModal.tab.${tabKey}`),
      initialOpen: activeTab === tabKey,
    };
  });

  if (hideLog) {
    mapped = mapped.filter(item => item.tabKey !== TASK_MODAL_TAB.LOG);
  }

  return mapped;
};

const getDefaultTaskModalMenuItems = (hideLog = false) =>
  applyTaskModalAccordionToMenuItems(
    [
      {
        tabKey: TASK_MODAL_TAB.PLAN,
        title: appT('task.home.taskModal.tab.plan'),
        isAccordion: true,
        initialOpen: false,
        items: [],
        action: () => {},
      },
      {
        tabKey: TASK_MODAL_TAB.ACTIVITY,
        title: appT('task.home.taskModal.tab.activity'),
        isAccordion: true,
        initialOpen: true,
        items: [],
        action: () => {},
      },
      {
        tabKey: TASK_MODAL_TAB.LOG,
        title: appT('task.home.taskModal.tab.log'),
        isAccordion: true,
        initialOpen: false,
        items: [],
        action: () => {},
      },
    ],
    hideLog,
  );

const taskModal = ref([]);
const activities_home = ref([]);
const focusActivity = ref('');
/** true면 해당 활동으로 스크롤만 하고 피드백 작성 버튼은 클릭하지 않음 */
const focusActivityScrollOnly = ref(false);
/** 홈 CollaborationTaskModal — 프로그램적 추가 로드 후 무한 스크롤 페이지 동기화용 */
const homeModalActivityLoadedPage = ref(1);
const homeModalActivityHasMore = ref(true);

/** 알림(PLAN) 클릭 시 보드 화면에서 처리 — tabId(C/S/M) */
const planNotifyRequest = ref<{ tabId: string } | null>(null);

/** 알림(ACTIVITY) 클릭 시 보드 화면에서 CollaborationTaskModal 조회 모드 오픈 */
const activityNotifyRequest = ref<{ planId: string; activityPath?: string } | null>(null);

const selectedHomeBoardId = ref([]);
/** 마지막 검색(돋보기) 시점의 보드 선택 — 사이드바 표시용 (드롭다운 변경만으로는 갱신 안 함) */
const appliedHomeBoardId = ref([]);
const selectedBoardId = ref('');

const syncAppliedHomeBoardFromSelection = () => {
  const sel = selectedHomeBoardId.value;
  if (Array.isArray(sel)) {
    appliedHomeBoardId.value = [...sel];
    return;
  }
  appliedHomeBoardId.value = sel ? [sel] : [];
};

// 보드 옵션 목록 (Header와 Sidebar에서 공유)
const homeBoardOptionsList = ref([]);

/** 홈 보드 미선택([]) — 전체([''])와 구분 (CollaborationSidebar selectedBoardList와 동일) */
const isHomeBoardUnselected = () => {
  return Array.isArray(selectedHomeBoardId.value) && selectedHomeBoardId.value.length === 0;
};

/** API findBoardId — 미선택 시 null(호출 생략), 전체 시 '' 또는 보드 ID 목록 */
const getHomeBoardIdsForApi = () => {
  if (isHomeBoardUnselected()) {
    return null;
  }
  if (Array.isArray(selectedHomeBoardId.value)) {
    return selectedHomeBoardId.value.filter(id => id !== '').join(',');
  }
  return selectedHomeBoardId.value || '';
};

// 보드 토글 상태 관리 (boardId -> expanded 상태)
const boardExpandedState = reactive({});

// 페이지 초기화 완료 여부 (초기 로드 중 자동 펼침 방지용)
const isHomePageReady = ref(false);

/** 활동 검색 후 사이드바에서 스크롤할 planId (CollaborationSidebar에서 소비 후 비움) */
const sidebarScrollToPlanIdAfterSearch = ref('');

// 보드 토글 상태 초기화 함수 (모두 접힌 상태로)
const resetBoardExpandedState = () => {
  Object.keys(boardExpandedState).forEach(key => {
    boardExpandedState[key] = false;
  });
};

// 필터 선택 상태
const selectedSubFilterId = ref('status-all'); // Header 상세 필터 선택 상태 (기본값: 전체)
const selectedResultStatusCd = ref('result-001'); // Index 왼쪽 메뉴 결과 상태 선택 (기본값: 전체)

// Header 필터가 적용된 planList와 activityList (Store reactive 변수)
const filteredHomePlanList = reactive([]) as any[];
// const filteredHomeActivityList = reactive([]) as any[];

const isHome = ref(false);
const isBoard = ref(false);

// 필터 드롭다운 열림 상태 (Header 상세 검색)
const isFilterOpen = ref(false);

// 아이콘 상태 관리 (Home 페이지용)
const iconStates = reactive({
  notification: false,
  copy: false,
  comment: false,
  settings: false,
});

/** 홈 plan/activity 검색 동시 실행 방지 (선택 상태 백업 race 방지) */
const homePlanSearchMutex = { serial: Promise.resolve() as Promise<unknown> };
const homeActivitySearchMutex = { serial: Promise.resolve() as Promise<unknown> };

const runHomeSearchSerialized = <T>(
  mutex: { serial: Promise<unknown> },
  fn: () => Promise<T>,
): Promise<T> => {
  const task = mutex.serial.then(fn, fn);
  mutex.serial = task.then(
    () => undefined,
    () => undefined,
  );
  return task;
};

const cardModals = ref([]);

// 보드 카테고리 (TaskBoard에서 사용)
const categories = ref([]);

// ----------- 추가된 함수: 카드의 activityUnreadCnt 및 currentTask의 planData 읽음 상태 초기화 -----------

/**
 * 카드의 activityUnreadCnt 및 currentTask 읽음 상태 초기화
 * 입력받은 card id와 일치하는 card의 activityUnreadCnt를 0으로 만들고,
 * currentTask.value의 planData가 해당 카드와 일치할 경우에도 초기화
 * @param {string} cardId - activityUnreadCnt를 초기화할 카드 ID
 */
const clearUnreadCountByCardId = (cardId: string) => {
  // 카테고리 내 cards 배열의 activityUnreadCnt 초기화
  if (categories.value && Array.isArray(categories.value)) {
    categories.value.forEach(category => {
      if (category.cards && Array.isArray(category.cards)) {
        category.cards.forEach(card => {
          if (card.id === cardId) {
            card.activityUnreadCnt = 0;
          }
        });
      }
    });
  }

  // currentTask의 planData 읽음 관련 값들도 0으로 초기화
  if (
    currentTask.value &&
    currentTask.value.planData &&
    (currentTask.value.planData.planId === cardId || currentTask.value.planData.id === cardId)
  ) {
    currentTask.value.planData.activityUnreadCnt = 0;
  }
};

const currentTask = ref(null);

// 모달 카운터 (팝업 관리용)
const floatingPopupCount = ref(0);
const fixedPopupCount = ref(0);

// Confirm 관련 상태
const confirm = ref({
  visible: false,
  type: 'info', // 'info', 'warning', 'error', 'success'
  title: '',
  message: '',
  showCancelButton: true,
});

// 결과 상태 코드 옵션
const resultStatusCdOptions = ref<{ label: string; value: string }[]>([]);

// 상태 코드 옵션
const statusCdOptions = ref<{ label: string; value: string; color: string }[]>([]);

/**
 * 홈 탭 검색 조건 초기화
 * 다른 메뉴/탭 이동 후 홈 탭 재진입 시 업무·활동 검색 조건을 초기화
 */
const resetHomeSearchConditions = () => {
  home.value.findPlanNm = '';
  home.value.findHomeActivityKeyword = '';
  home.value.findHomeActivityStartDt = '';
  home.value.findHomeActivityEndDt = '';
  hasSearchedPlanNm.value = false;
  isFilterActiveSearch.value = false;
  isNewClick.value = 'N';
  isFilterOpen.value = false;
  selectedSubFilterId.value = 'status-all';
  selectedResultStatusCd.value = 'result-001';
  iconStates.notification = false;
  iconStates.copy = false;
  iconStates.comment = false;
  iconStates.settings = false;
};

/** selectHomeData 응답 boardSelectSelectedList → selectedHomeBoardId 반영 */
const applyBoardSelectSelectedList = (boardSelectSelectedList: unknown) => {
  if (!Array.isArray(boardSelectSelectedList)) return;

  const boardIds = boardSelectSelectedList
    .map((item: { boardId?: string } | string) => (typeof item === 'string' ? item : item?.boardId))
    .filter((id): id is string => id != null && String(id).trim() !== '');

  selectedHomeBoardId.value = boardIds;
  syncAppliedHomeBoardFromSelection();
};

/**
 * Store 초기화 함수 (Plugin에서 자동 호출)
 * 목적: 페이지 진입 시 필요한 공통 데이터 로드
 */
const initialize = async () => {
  const codeStore = useCodeStore();
  try {
    // 서로 독립적인 두 조회를 병렬 실행 (코드목록 + 홈 상태목록) — 직렬 await 제거로 초기화 단축
    const params = new URLSearchParams();
    const [resultStatusCodeList, response] = await Promise.all([
      codeStore.getCodeList('387'),
      $api.fetchHomeData(params),
    ]);

    // resultStatusCdOptions 초기화
    resultStatusCdOptions.value = resultStatusCodeList.map((code: any) => ({
      label: code.codeNm,
      value: code.codeId,
    }));

    // statusCdOptions 초기화
    if (response?.readGbn) {
      readGbn.value = response.readGbn;
    }

    if (response?.statusList) {
      statusCdOptions.value = response.statusList.map((item: any) => ({
        label: item.statusNm,
        value: item.statusCd,
        color: item.statusColor,
      }));
    }

    if (response?.boardSelectSelectedList !== undefined) {
      applyBoardSelectSelectedList(response.boardSelectSelectedList);
    }

    refreshSubFilters();
    home.value.resultFilters = await generateResultFilters(home.value.planList || []);
  } catch (error) {
    console.error('❌ 상태 목록 조회 실패:', error);
  }
};

const isBoardAddModalOpen = ref(false);
/**
 * 보드 추가 모달 토글 함수
 */
const openBoardAddModal = () => {
  // 보드 없음 튜토리얼(driver.js)이 + 버튼을 가리고 있을 때, 모달만 열리고 오버레이가 남는 문제 방지
  const { destroyActiveTutorial } = useTutorial();
  destroyActiveTutorial();
  isBoardAddModalOpen.value = !isBoardAddModalOpen.value;
};

// 활동 첨부파일 상세 모달
const isFileAttachmentModalOpen_detailActivity = ref(false);
const attachmentFiles_detailActivity: Ref<File[]> = ref([]);

// 활동 수정모드 첨부파일 모달
const isFileAttachmentModalOpen_editActivity = ref(false);
const attachmentFiles_editActivity: Ref<File[]> = ref([]);

// 피드백/답글 첨부파일 모달 (활동 추가 첨부와 동일 UI)
const isFileAttachmentModalOpen_feedback = ref(false);
const attachmentFiles_feedback: Ref<File[]> = ref([]);
const feedbackAttachmentApplyHandler = ref<(() => void) | null>(null);
const feedbackAttachmentCancelHandler = ref<(() => void) | null>(null);

const confirmFeedbackAttachmentModal = () => {
  feedbackAttachmentApplyHandler.value?.();
  feedbackAttachmentApplyHandler.value = null;
  feedbackAttachmentCancelHandler.value = null;
  isFileAttachmentModalOpen_feedback.value = false;
};

const cancelFeedbackAttachmentModal = () => {
  feedbackAttachmentCancelHandler.value?.();
  feedbackAttachmentApplyHandler.value = null;
  feedbackAttachmentCancelHandler.value = null;
  isFileAttachmentModalOpen_feedback.value = false;
};

/** 홈 활동/피드백 피드에서 첨부파일 조회 모달 열기 */
const handleOpenDetailAttachment = async (atchFileId: string) => {
  if (isEmpty(atchFileId)) return;

  isFileAttachmentModalOpen_detailActivity.value = true;
  attachmentFiles_detailActivity.value = [];

  const atchFileList = await fetchAtchFileList({ atchFileId: String(atchFileId) });
  attachmentFiles_detailActivity.value = atchFileList?.fileList || [];
};

/**
 * 필터 활성화 상태를 확인하는 computed 속성
 * 필터 값이 하나라도 설정되어 있으면 true 반환
 */
const isFilterActive = computed(() => {
  if (!searchBox.value) return false;

  // 업무상태 필터 확인
  const hasStatus = searchBox.value.findAvtPlanStatus && searchBox.value.findAvtPlanStatus !== '';

  // D-DAY 필터 확인
  const hasDDay = searchBox.value.findDay && searchBox.value.findDay !== '';

  const hasResultStatus =
    Array.isArray(searchBox.value.findResultStatusCd) && searchBox.value.findResultStatusCd.length > 0;

  const hasProgressStatus =
    Array.isArray(searchBox.value.findStatusCd) && searchBox.value.findStatusCd.length > 0;

  const hasCoopShareUser =
    Array.isArray(searchBox.value.findCoopShareUserId) && searchBox.value.findCoopShareUserId.length > 0;

  return hasStatus || hasDDay || hasResultStatus || hasProgressStatus || hasCoopShareUser;
});

/** 보드 검색필터 멀티선택 값 → API 콤마 구분 문자열 */
const joinBoardSearchFilterValues = (value: string | string[] | null | undefined) => {
  if (Array.isArray(value)) {
    return value.filter(v => isNotEmpty(v)).join(',');
  }
  return isNotEmpty(value) ? String(value) : '';
};

/** 검색필터 팝업 로컬 상태 초기화 */
const resetBoardSearchFilterState = () => {
  searchBox.value.findAvtPlanStatus = '';
  searchBox.value.findDay = '';
  searchBox.value.findResultStatusCd = [];
  searchBox.value.findStatusCd = [];
  searchBox.value.findCoopShareUserId = [];
  home.value.findFromDt = '';
  home.value.findToDt = '';
  isFilterActiveSearch.value = false;
};

/** 실행기간 DB(hcm_avt_board_term) 초기화 — selectData.do → updateBoardTerm */
const handleClearBoardSearchTermDb = async () => {
  const params = new URLSearchParams();
  params.append('findYear', findYear.value);
  params.append('findBoardId', searchBox.value.findBoardId || status.boardId || '');
  params.append('findDtGubun', '02');
  params.append('findFromDt', '');
  params.append('findToDt', '');
  await $api.fetchClearBoardSearchTerm(params);
};

/** 검색필터 전체 초기화 (UI + DB 실행기간) */
const handleResetBoardSearchFilter = async () => {
  resetBoardSearchFilterState();
  await handleClearBoardSearchTermDb();
};

const isFilterActiveSearch = ref(false); // 검색필터를 적용하고 검색 여부

const getTaskStatusColor = (color: string) => {
  return nvl(color, '#9f9fa7');
};

// 읽음 처리 통합 함수
const handleReadArticle = async (options: any) => {
  const { readUnit, boardId, planId, activityNo, replyNo, upReplyNo } = options;

  const params = {
    readUnit: readUnit,
    boardId: boardId,
    planId: planId,
    activityNo: activityNo,
    replyNo: replyNo,
    upReplyNo: upReplyNo,
  };

  try {
    await $api.insertAvtActivityRead(params);

    // readUnit에 따른 콜백 처리
    switch (readUnit) {
      case 'all':
        // 전체 레벨 읽음 처리 후 콜백
        filteredHomePlanList.forEach((item, index) => {
          filteredHomePlanList[index] = { ...item, activityUnreadCnt: 0 };
        });
        break;

      case 'board':
        // board 레벨 읽음 처리 후 콜백
        filteredHomePlanList.forEach((item, index) => {
          filteredHomePlanList[index] = {
            ...item,
            activityUnreadCnt: item.boardId === boardId ? 0 : item.activityUnreadCnt,
          };
        });
        break;
      case 'plan':
        // plan 읽음 처리
        filteredHomePlanList.forEach((item, index) => {
          filteredHomePlanList[index] = {
            ...item,
            activityUnreadCnt: item.id === planId ? 0 : item.activityUnreadCnt,
          };
        });

        // activity 읽음 처리
        home.value.activityList = home.value.activityList.map(e => ({
          ...e,
          activityUnreadCnt: e.planId === planId ? 0 : e.activityUnreadCnt,
        }));

        break;

      case 'activity':
        // activity 레벨 읽음 처리 후 콜백

        break;

      case 'feedback':
        // feedback 레벨 읽음 처리 후 콜백

        break;

      case 'reply':
        // reply 레벨 읽음 처리 후 콜백

        break;

      default:
        console.warn('[handleReadArticle] Unknown readUnit:', readUnit);
    }
  } catch (error) {
    console.error('[handleReadArticle] Failed:', error);
  }
};

// 홈 모달 데이터 초기화
const homeTaskModalClear = async (reloadActivity = true) => {
  homeStatus.value.planId = '';
  isTaskModalOpen.value = false;
  isTaskModalSwitching.value = false;
  collabTaskModalActivityReadOnly.value = false;
  collabTaskModalFeedbackReadOnly.value = false;
  collabTaskModalHideLog.value = false;
  collabTaskModalAllowFloating.value = false;
  collabTaskModalShowActivityEditDelete.value = false;
  collabTaskModalShowPlanReadNew.value = false;
  taskModal.value = {};
  selectedBoardId.value = null;
  await nextTick();
  selectedBoardId.value = homeStatus.value.boardId;

  // 홈 활동 목록 리로드 (활동 저장/삭제 후 CollaborationFeed의 filteredActivities 업데이트)
  if (reloadActivity) {
    await handleActivitySearch(true);
  }
};

/**
 * 헤더 상세 필터 적용 함수
 *
 * 역할:
 * 1. selectedSubFilterId (Header 상세 필터)와 selectedResultStatusCd (Index 필터) 두 단계 모두 적용
 * 2. 필터링과 UI 포맷 변환을 모두 filteredHomePlanList에 저장
 */
const applyHeaderFilters = (
  savedPlanSelection?: Map<string, { selected: boolean; showActivityList: boolean }>,
) => {
  // 1단계: Header 상세 필터 (selectedSubFilterId)에 따라 planList 필터링
  let filteredList = home.value.planList;
  let subFilteredList = home.value.planList; // subFilter만 적용된 리스트 (generateResultFilters용)

  // selectedSubFilterId가 "status-"로 시작하면 statusCd 필터링
  if (selectedSubFilterId.value && selectedSubFilterId.value.startsWith('status-')) {
    const statusCd = selectedSubFilterId.value.substring(7); // "status-" 이후의 문자열 추출

    if (statusCd === 'all') {
      // 전체: 필터링하지 않음
      filteredList = home.value.planList;
      subFilteredList = home.value.planList;
    } else if (statusCd === 'owner') {
      // 내 업무
      filteredList = filteredList.filter(plan => plan.ownerYn === 'Y');
      subFilteredList = home.value.planList.filter(plan => plan.ownerYn === 'Y');
    } else if (statusCd === 'coop') {
      // 협업 업무
      filteredList = filteredList.filter(plan => plan.ownerYn === 'N');
      subFilteredList = home.value.planList.filter(plan => plan.ownerYn === 'N');
    } else if (statusCd === 'delay') {
      // 기간초과
      filteredList = filteredList.filter(plan => isNotEmpty(plan.dateStatus));
      subFilteredList = home.value.planList.filter(plan => isNotEmpty(plan.dateStatus));
    } else if (statusCd === 'imp') {
      // 중요업무
      filteredList = filteredList.filter(plan => plan.importYn === 'Y');
      subFilteredList = home.value.planList.filter(plan => plan.importYn === 'Y');
    } else {
      // 상태 코드 (S001, S002 등)
      filteredList = filteredList.filter(plan => plan.statusCd === statusCd);
      subFilteredList = home.value.planList.filter(plan => plan.statusCd === statusCd);
    }
  }

  // 2단계: Index 왼쪽 메뉴 필터 (selectedResultStatusCd)에 따라 추가 필터링 (filteredList만 적용)
  const resultStatusCd = selectedResultStatusCd.value.substring(7); // "result-" 이후의 문자열 추출
  if (resultStatusCd != 'all') {
    filteredList = filteredList.filter(plan => plan.resultStatusCd === resultStatusCd);
  }

  // 3단계: 필터링된 데이터를 UI용 포맷으로 변환하여 filteredHomePlanList에 저장
  const formattedList = filteredList.map((item: any) => {
    const saved = savedPlanSelection?.get(String(item.planId));
    return {
      id: item.planId,
      boardId: item.boardId,
      title: item.planNm,
      author:
        item.ownerYn == 'Y' ? '내 업무' : `${item.userNm} | ${getDeptNmByUserId(item.createUserId)}`,
      date: formatDate(item.endDt),
      endDt: item.endDt,
      statusColor: item.statusColor,
      statusText: item.resultStatusNm,
      selected: savedPlanSelection ? (saved?.selected ?? false) : true,
      resultStatusCd: item.resultStatusCd || '005',
      resultStatusNm: getResultStatusName(item.resultStatusCd, item.resultStatusNm),
      activityCnt: item.activityCnt || 0,
      showActivityList: savedPlanSelection ? (saved?.showActivityList ?? false) : true,
      activityUnreadCnt: item.activityUnreadCnt || 0,
      newActivityCnt: item.newActivityCnt || 0,
      newFeedbackCnt: item.newFeedbackCnt || 0,
      ownerYn: item.ownerYn,
      planType: item.planType,
      importYn: item.importYn,
      statusCd: item.statusCd,
      planNm: item.planNm,
      createUserId: item.createUserId,
      userNm: item.userNm,
    };
  });
  filteredHomePlanList.splice(0, filteredHomePlanList.length, ...formattedList);

  // 4단계: 선택된 subFilter만 적용된 subFilteredList를 기반으로 resultFilters 업데이트
  generateResultFilters(subFilteredList).then(resultFilters => {
    home.value.resultFilters = resultFilters;
  });
};

// 헬퍼 함수들
const getDeptNmByUserId = (userId: string) => {
  if (!userList.value || !userId) return '';
  const user = userList.value.find((u: any) => u.userId === userId);
  return user ? user.deptNm : '';
};

/** 전달·협업 선택용 사용자 목록 (부서 노드 제외) */
const handleInitTransferUserList = async () => {
  try {
    const userData = await $api.fetchInitUserList();
    transferUserList.value = userData.list || [];
  } catch (e) {
    console.error('전달용 사용자 목록 조회 실패:', e);
    transferUserList.value = [];
  }
};

const findTransferUserNm = (userId: string) => {
  if (!userId) return '';
  const id = String(userId);
  const fromTransfer = transferUserList.value.find((u: any) => String(u.userId) === id);
  if (fromTransfer?.userNm) return fromTransfer.userNm;
  const fromAll = userList.value.find((u: any) => String(u.userId) === id);
  return fromAll?.userNm ?? '';
};

/** API 전송용 표시명: 사용자명[조직] */
const findTransferUserLabel = (userId: string) => {
  if (!userId) return '';
  const id = String(userId);
  const fromTransfer = transferUserList.value.find((u: any) => String(u.userId) === id);
  if (fromTransfer) return formatTransferUserLabel(fromTransfer);
  const fromAll = userList.value.find((u: any) => String(u.userId) === id);
  if (fromAll) return formatTransferUserLabel(fromAll);
  return id;
};

/** 전달·협업 사용자 셀렉트 표시: 사용자명[조직] */
const formatTransferUserLabel = (user: Record<string, unknown> | null | undefined) => {
  if (!user) return '';
  const userDeptNm = String(user.userDeptNm ?? '').trim();
  if (userDeptNm.includes('[')) return userDeptNm;

  const name = String(user.userNm ?? '').trim();
  const dept = String(user.deptNm ?? user.deptFNm ?? '').trim();
  if (!name) return String(user.userId ?? '');
  if (!dept) return name;
  return `${name}[${dept}]`;
};

const formatDate = (dateStr: string) => {
  // isEmptyEndDt: 날짜가 비어있으면 '--' 반환
  if (!dateStr || isEmpty(dateStr)) return '--';

  // isOverdue: 기간 초과 여부 확인
  const [year, month, day] = dateStr.split('.').map(num => parseInt(num));
  const endDate = new Date(year, month - 1, day); // month는 0-based이므로 -1
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const isOverdue = endDate < today;

  // 기간 초과면 '기간초과' 반환
  if (isOverdue) return TASK_OVERDUE_DATE_LABEL;

  // 그 외에는 `~ ${포맷된 날짜}` 형식으로 반환
  return `~ ${year}년 ${month}월 ${day}일`;
};

/** 진행상태명 — 공통코드(387)는 고정 코드라 i18n, 그 외는 서버명 */
const getResultStatusName = (resultStatusCd: string, fallbackNm?: string) => {
  return translateResultStatusLabel(resultStatusCd, fallbackNm);
};

/**
 * subFilters 생성 함수
 * planList를 기반으로 각 필터의 count를 계산하여 subFilters 배열 생성
 */
const generateSubFilters = (planList: any[]) => {
  // 각 필터별 count 계산
  const statusAll = planList ? planList.length : 0;

  // statusCdOptions.value를 사용하여 동적으로 상태 필터 생성 (color 포함)
  const statusFilters = statusCdOptions.value.map(status => ({
    key: `status-${status.value}`,
    label: status.label,
    count: planList ? planList.filter(plan => plan.statusCd === status.value).length : 0,
    color: status.color || '',
  }));

  const statusOwner = planList ? planList.filter(plan => plan.ownerYn === 'Y').length : 0;
  const statusCoop = planList ? planList.filter(plan => plan.ownerYn === 'N').length : 0;
  const statusImp = planList ? planList.filter(plan => plan.importYn === 'Y').length : 0;

  // 지연 업무: dateStatus가 있는 업무 (applyHeaderFilters와 동일한 조건)
  const statusDelay = planList ? planList.filter(plan => isNotEmpty(plan.dateStatus)).length : 0;

  // subFilters 배열 생성 (백엔드와 동일한 순서)
  const result = [
    { key: 'status-all', label: '전체', count: statusAll },
    ...statusFilters,
    { key: 'status-owner', label: '내 업무', count: statusOwner },
    { key: 'status-coop', label: '협업 업무', count: statusCoop },
    { key: 'status-imp', label: '중요 업무', count: statusImp },
    { key: 'status-delay', label: '지연 업무', count: statusDelay },
  ];
  return result;
};

/**
 * resultFilters 생성 함수
 * 선택된 subFilter에 해당하는 planList를 기반으로 각 결과 상태별 count를 계산하여 resultFilters 배열 생성
 * @param filteredPlanList - subFilter에 따라 필터링된 planList
 */
const generateResultFilters = async (filteredPlanList: any[]) => {
  const codeStore = useCodeStore();

  try {
    // codeStore에서 resultStatusList 가져오기
    const resultStatusCodeList = await codeStore.getCodeList('387');

    if (!filteredPlanList || filteredPlanList.length === 0) {
      // resultStatusCodeList를 사용하여 동적으로 빈 필터 생성
      return [
        { key: 'result-all', label: '전체', count: 0 },
        ...resultStatusCodeList.map((resultStatus: any) => ({
          key: `result-${resultStatus.codeId}`,
          label: resultStatus.codeNm,
          count: 0,
        })),
      ];
    }

    // 전체 count
    const resultAll = filteredPlanList.length;

    // resultStatusCodeList를 사용하여 동적으로 결과 상태 필터 생성
    const resultStatusFilters = resultStatusCodeList.map((resultStatus: any) => ({
      key: `result-${resultStatus.codeId}`,
      label: resultStatus.codeNm,
      count: filteredPlanList.filter(plan => plan.resultStatusCd === resultStatus.codeId).length,
    }));

    return [
      { key: 'result-all', label: '전체', count: resultAll },
      ...resultStatusFilters,
    ];
  } catch (error) {
    console.error('❌ resultFilters 생성 실패:', error);
    return [{ key: 'result-all', label: '전체', count: 0 }];
  }
};

/** 상단 상세필터(subFilters) 재생성 — planList 기준 */
const refreshSubFilters = () => {
  home.value.subFilters = generateSubFilters(home.value.planList || []);
};

/** 보드 미선택·조회 결과 없음 시 좌측/헤더 필터·목록 초기화 */
const clearHomeSearchResults = async (searchYn?: string) => {
  filteredHomePlanList.length = 0;
  home.value.planList = [];
  home.value.activityList = [];
  home.value.subFilters = generateSubFilters([]);
  home.value.combinedFilters = [];
  home.value.planListRowsExist = false;
  home.value.activityListRowsExist = false;
  home.value.planListPage = 1;
  home.value.activityListPage = 1;

  if (searchYn === 'Y') {
    selectedSubFilterId.value = 'status-all';
    selectedResultStatusCd.value = 'result-001';
  }

  home.value.resultFilters = await generateResultFilters([]);
};

// 새 카드 모달 열기 (카테고리 ID 전달)
const openNewCardModal = (categoryId: string) => {
  // 메뉴 설정
  const menuItems_add = ref([
    {
      tabKey: TASK_MODAL_TAB.PLAN,
      title: appT('task.board.taskModal.tab.plan'),
      isAccordion: true,
      initialOpen: true,
      items: [],
      action: () => {},
    },
    {
      tabKey: TASK_MODAL_TAB.ACTIVITY,
      title: appT('task.board.taskModal.tab.activity'),
      isAccordion: true,
      initialOpen: false,
      items: [],
      action: () => {},
    },
  ]);

  cardModals.value = []; // 상세 모달 열려있으면 닫기

  // 오늘 날짜를 yyyy.mm.dd 형식으로 생성
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}.${mm}.${dd}`;

  const planData_add = {
    categoryId: categoryId,
    startDt: todayStr,
  };

  const card_add = {
    title: '카드 추가',
  };

  cardModals.value.push({
    id: Date.now(),
    card: card_add,
    isOpen: true,
    isFloating: false,
    zIndex: getNextCardModalZIndex(),
    planData: planData_add,
    menuItems: menuItems_add.value,
    modalType: 'add',
  });
};

// 카드 상세 데이터 조회
const handleFetchPlan = async (cardId: string) => {
  const params = {
    findYear: findYear.value,
    findBoardId: status.boardId,
    findMon: status.planActivityMon,
    findAnalCycle: '',
    planId: cardId,
  };

  const planResponse = await $api.fetchPlan(params);

  if (planResponse.planData) {
    plan.value.planId = planResponse.planData.planId;
    plan.value.planNm = planResponse.planData.planNm;
    plan.value.categoryId = planResponse.planData.categoryId;
    plan.value.categoryNm = planResponse.planData.categoryNm;
    plan.value.startDt = planResponse.planData.startDt;
    plan.value.endDt = planResponse.planData.endDt;
    plan.value.details = planResponse.planData.details;
    plan.value.authCoop = planResponse.planData.authCoop;
    plan.value.authShare = planResponse.planData.authShare;
    plan.value.activityCnt = planResponse.planData.activityCnt;
    plan.value.ownerYn = planResponse.planData.ownerYn;
    plan.value.activityUnreadCnt = planResponse.planData.activityUnreadCnt;
    plan.value.planType = planResponse.planData.planType;
    plan.value.ownerUserId = planResponse.planData.userId;

    // readGbn은 selectHomeData(initialize) 회사 설정값 유지 — selectPlan이 '01' 등으로 덮어쓰면 읽음 표시 사라짐
    if (planResponse.authGbn) {
      authGbn.value = planResponse.authGbn;
    }
    console.log('[selectPlan.do] handleFetchPlan authGbn', {
      planId: cardId,
      authGbn: planResponse.authGbn,
      storedAuthGbn: authGbn.value,
    });
    status.planId = planResponse.planData.planId;
  }

  return planResponse;
};

/** 업무 활동 로그 조회 (로그 탭 펼칠 때) */
const handleFetchAvtLogList = async (planId: string) => {
  if (!planId) {
    avtLogList.value = [];
    return;
  }

  try {
    const res = await $api.fetchAvtLogList({
      findYear: findYear.value,
      planId,
    });
    avtLogList.value = res?.avtLogList || [];
  } catch (e) {
    console.error('활동 로그 조회 실패:', e);
    avtLogList.value = [];
  }
};

/** CollaborationTaskModal 활동 목록 — openCollaborationTaskModal·무한 스크롤 공통 파라미터 */
const resolveHomeTaskModalBoardIds = (planData?: { boardId?: string; boardCd?: string }) => {
  const boardIdsFromHome = Array.isArray(selectedHomeBoardId.value)
    ? selectedHomeBoardId.value.filter(id => id !== '').join(',')
    : String(selectedHomeBoardId.value || '');
  const planBoardId = String(planData?.boardId || planData?.boardCd || '').trim();
  return boardIdsFromHome || planBoardId;
};

/** 홈 모달 무한 스크롤 — 페이지별 raw 누적 후 전체 재매핑용 */
const homeActivityListRaw = ref<any[]>([]);
const homeActivityListRawPlanId = ref('');
const HOME_MODAL_ACTIVITY_ROWS = 50;

type FetchHomeActivityListOpts = {
  rows?: number;
  skipLoading?: boolean;
};

const fetchHomeActivityListRaw = async (
  planId: string,
  page?: number,
  planData?: { boardId?: string; boardCd?: string },
  opts?: FetchHomeActivityListOpts,
) => {
  const rows = opts?.rows ?? HOME_MODAL_ACTIVITY_ROWS;
  const activityParams = new URLSearchParams();
  activityParams.append('findYear', findYear.value);
  activityParams.append('findBoardId', resolveHomeTaskModalBoardIds(planData));
  activityParams.append('findMon', '');
  activityParams.append('findAnalCycle', '');
  activityParams.append('planId', planId);
  activityParams.append('page', page ? String(page) : '1');
  activityParams.append('rows', String(rows));

  const activityResponse = await $api.fetchActivityList(activityParams, {
    skipLoading: opts?.skipLoading === true,
  });
  return activityResponse?.activityList || [];
};

const handleFetchHomeActivityList = async (
  planId: string,
  page?: number,
  planData?: { boardId?: string; boardCd?: string },
) => {
  const raw = await fetchHomeActivityListRaw(planId, page, planData);
  return mapActivityListForCards(raw);
};

/** 홈 모달 — raw 누적 후 mapActivityListForCards 전체 재매핑 (댓글·replyCnt 정합) */
const handleFetchHomeActivityListAccumulated = async (
  planId: string,
  page?: number,
  planData?: { boardId?: string; boardCd?: string },
) => {
  const pageNum = page ? Number(page) : 1;
  if (pageNum <= 1 || homeActivityListRawPlanId.value !== planId) {
    homeActivityListRaw.value = [];
    homeActivityListRawPlanId.value = planId;
  }

  const rawPage = await fetchHomeActivityListRaw(planId, pageNum, planData);
  homeActivityListRaw.value =
    pageNum <= 1 ? rawPage : [...homeActivityListRaw.value, ...rawPage];

  return {
    items: mapActivityListForCards(homeActivityListRaw.value),
    rawPageLength: rawPage.length,
  };
};

/** 보드 TaskModal 무한 스크롤 — planId별 raw 누적 */
const boardActivityListRawMap = new Map<string, any[]>();

const fetchBoardActivityListRaw = async (cardId: string, page?: number) => {
  const params = new URLSearchParams();
  params.append('findYear', findYear.value);
  params.append('findBoardId', status.boardId);
  params.append('findBoardIds', status.boardId);
  params.append('findMon', status.planActivityMon);
  params.append('planId', cardId);
  params.append('page', page ? String(page) : String(board.value.activityListLimit));
  params.append('rows', '50');

  const activityResponse = await $api.fetchActivityList(params);
  return activityResponse?.activityList || [];
};

// 활동 데이터 목록 조회 (단일 페이지 — 카드 저장/갱신 등)
const handleFetchActivityList = async (cardId, page) => {
  const raw = await fetchBoardActivityListRaw(cardId, page);
  plan.value.activityList = raw;
  boardActivityListRawMap.set(String(cardId), [...raw]);
  return mapActivityListForCards(raw);
};

/** 보드 TaskModal — raw 누적 후 전체 재매핑 (댓글·replyCnt 정합) */
const handleFetchActivityListAccumulated = async (cardId: string, page?: number) => {
  const pageNum = page ? Number(page) : 1;
  const key = String(cardId);
  const rawPage = await fetchBoardActivityListRaw(cardId, pageNum);

  if (pageNum <= 1) {
    boardActivityListRawMap.set(key, [...rawPage]);
  } else {
    const prev = boardActivityListRawMap.get(key) || [];
    boardActivityListRawMap.set(key, [...prev, ...rawPage]);
  }

  const combined = boardActivityListRawMap.get(key) || [];
  if (pageNum <= 1) {
    plan.value.activityList = rawPage;
  }

  return {
    items: mapActivityListForCards(combined),
    rawPageLength: rawPage.length,
  };
};

/**
 * 활동 키워드 검색 결과(collaboration-item/채팅과 동일한 activityList)의 planId에 해당하는
 * 사이드바 보드(task-list)를 펼침. 업무 행만 맞고 피드에 없는 경우는 대상 아님.
 */
const expandSidebarBoardsForSearchedActivities = (isInitialSearch: boolean) => {
  sidebarScrollToPlanIdAfterSearch.value = '';
  if (!isInitialSearch) return;

  const kw = (home.value.findHomeActivityKeyword || '').trim();
  if (!kw) return;

  const list = home.value.activityList || [];
  if (!list.length) return;

  const seenPid = new Set<string>();
  const orderedPlanIds: string[] = [];
  list.forEach((a: any) => {
    const pid = String(a.planId || '');
    if (!pid || seenPid.has(pid)) return;
    seenPid.add(pid);
    orderedPlanIds.push(pid);
  });

  orderedPlanIds.forEach(pid => {
    const task = filteredHomePlanList.find((t: any) => String(t.id) === pid);
    if (task?.boardId != null && task.boardId !== '') {
      boardExpandedState[String(task.boardId)] = true;
    }
  });

  if (orderedPlanIds.length > 0) {
    sidebarScrollToPlanIdAfterSearch.value = orderedPlanIds[0];
  }
};

const isSyncingHomeDefaultData = ref(false);

/** newHomeActivityList / selectHomeDefaultData 응답의 홈 기본 검색 조건 반영 */
const applyHomeDefaultDataResult = (data: any) => {
  if (!data) return;

  isSyncingHomeDefaultData.value = true;

  if (data.homeActivityDtKeepYn !== undefined) {
    // Y만 유지 ON — 없거나 null/빈값/N 은 모두 N으로 동작
    home.value.homeActivityDtKeepYn = data.homeActivityDtKeepYn === 'Y' ? 'Y' : 'N';
  }

  // KeepYn=Y일 때만 서버 저장 날짜 반영 (N/없거나 null: 클라이언트 값 유지)
  if (home.value.homeActivityDtKeepYn === 'Y') {
    if (data.findHomeActivityStartDt !== undefined) {
      home.value.findHomeActivityStartDt = data.findHomeActivityStartDt || '';
    }
    if (data.findHomeActivityEndDt !== undefined) {
      home.value.findHomeActivityEndDt = data.findHomeActivityEndDt || '';
    }
  }

  nextTick(() => {
    isSyncingHomeDefaultData.value = false;
  });
};

/** 홈 활동 검색 유지하기·기간 저장 (saveHomeDefaultDataInfo.do) */
const handleSaveHomeDefaultData = async () => {
  const params = new URLSearchParams();
  params.append('findHomeActivityStartDt', home.value.findHomeActivityStartDt || '');
  params.append('findHomeActivityEndDt', home.value.findHomeActivityEndDt || '');
  params.append('homeActivityDtKeepYn', home.value.homeActivityDtKeepYn === 'Y' ? 'Y' : 'N');

  try {
    await $api.saveHomeDefaultData(params);
  } catch (e) {
    console.error('홈 활동 검색 조건 저장 실패:', e);
    showAlert(appT('common.error'), appT('task.home.alert.saveSearchConditionFailed'));
  }
};

/**
 * 활동 검색 함수 (초기 검색)
 * 페이지를 1로 초기화하고 새로운 검색을 시작
 * @param isInitialSearch - 초기 검색 여부
 */
const handleActivitySearchImpl = async (isInitialSearch = true) => {
  // 초기 검색이면 page를 1로 리셋
  if (isInitialSearch) {
    home.value.activityListPage = 1;
  }

  if (isHomeBoardUnselected()) {
    if (isInitialSearch) {
      home.value.activityList = [];
    }
    home.value.activityListRowsExist = false;
    return;
  }

  // selected 상태인 filteredHomePlanList의 planId 수집
  const planIdList = filteredHomePlanList.filter(item => item.selected).map(item => item.id);

  // 조회된 업무(planId)가 없으면 API 호출 없이 피드 비움 (빈 findBoardId는 서버에서 전체 조회로 처리됨)
  if (isInitialSearch && planIdList.length === 0) {
    home.value.activityList = [];
    home.value.activityListRowsExist = false;
    return;
  }

  // fetchActivityList 호출
  const params = new URLSearchParams();
  params.append('findBoardId', getHomeBoardIdsForApi() ?? '');
  params.append('findPlanListGbnCd', 'home');
  params.append('findHomeActivityStartDt', home.value.findHomeActivityStartDt);
  params.append('findHomeActivityEndDt', home.value.findHomeActivityEndDt);
  params.append('findHomeActivityKeyword', home.value.findHomeActivityKeyword);
  params.append('homeActivityDtKeepYn', home.value.homeActivityDtKeepYn === 'Y' ? 'Y' : 'N');

  // 아이콘 필터 (선택된 값 하나만 전달)
  let findIconFilter = null;
  if (iconStates.notification) {
    findIconFilter = 'notification';
  } else if (iconStates.copy) {
    findIconFilter = 'copy';
  } else if (iconStates.comment) {
    findIconFilter = 'comment';
  } else if (iconStates.settings) {
    findIconFilter = 'settings';
  }
  if (findIconFilter) {
    params.append('findIconFilter', findIconFilter);
  } else {
    params.append('findIconFilter', '');
  }

  params.append('rows', '50');
  params.append('page', String(home.value.activityListPage));
  planIdList.forEach(planId => {
    params.append('planIdList', planId);
  });

  try {
    const response = await $api.fetchHomeActivityList(params);

    if (isInitialSearch) {
      applyHomeDefaultDataResult(response);
    }

    // 응답 데이터로 home.value.activityList 업데이트
    if (response && response.activityList) {
      let newActivityList = [];
      if (isInitialSearch) {
        // 초기 검색: 기존 데이터 교체
        newActivityList = response.activityList;
      } else {
        // 추가 로드: 기존 데이터에 추가
        newActivityList = [...home.value.activityList, ...response.activityList];
      }

      // sameActivityNoYn 설정을 위한 활동 목록 만들기 (planId, activityNo 조합)
      // 전체 activityList 기준으로 활동 목록 생성
      const activitiesMap = new Map(
        newActivityList
          .filter(
            (activity: any) =>
              !activity.replyNo || activity.replyNo === 0 || activity.replyNo === '',
          )
          .map((activity: any) => [`${activity.planId}_${activity.activityNo}`, activity]),
      );

      // 댓글/대댓글 Map (upUserNm, upContent 매핑용)
      // key: planId_activityNo_replyNo
      const replyMap = new Map(
        newActivityList
          .filter(
            (activity: any) =>
              activity.replyNo && activity.replyNo !== 0 && activity.replyNo !== '',
          )
          .map((activity: any) => [
            `${activity.planId}_${activity.activityNo}_${activity.replyNo}`,
            activity,
          ]),
      );

      // 각 activity에 sameActivityNoYn 및 upUserNm, upContent 설정
      home.value.activityList = newActivityList.map((activity: any) => {
        const result = { ...activity };

        if (activity.replyNo && activity.replyNo !== 0 && activity.replyNo !== '') {
          // replyNo가 있는 경우 (댓글/대댓글)
          const activityKey = `${activity.planId}_${activity.activityNo}`;
          const hasSameActivity = activitiesMap.has(activityKey);
          result.sameActivityNoYn = hasSameActivity ? 'Y' : 'N';

          // upUserNm, upContent 매핑
          if (activity.upReplyNo && activity.upReplyNo !== 0 && activity.upReplyNo !== '') {
            // 대댓글인 경우: 상위 댓글에서 정보 가져오기
            const upReplyKey = `${activity.planId}_${activity.activityNo}_${activity.upReplyNo}`;
            const upReply = replyMap.get(upReplyKey);
            if (upReply) {
              result.upContent = upReply.content;
              result.upUserNm = upReply.userNm;
              result.upCreateDt = upReply.createDt;
              result.upCreateUserId = upReply.createUserId;
            }
          } else {
            // 첫 번째 댓글인 경우: 상위 활동에서 정보 가져오기
            const upActivity = activitiesMap.get(activityKey);
            if (upActivity) {
              result.upContent = upActivity.content;
              result.upUserNm = upActivity.userNm;
              result.upCreateDt = upActivity.createDt;
              result.upCreateUserId = upActivity.createUserId;
            }
          }
        } else {
          // replyNo가 없는 경우 (활동)
          result.sameActivityNoYn = 'Y';
        }

        return result;
      });

      // 더 가져올 데이터가 있는지 확인 (50개 미만이면 더 이상 없음)
      home.value.activityListRowsExist = response.activityList.length >= 50;

      expandSidebarBoardsForSearchedActivities(isInitialSearch);
    } else {
      if (isInitialSearch) {
        home.value.activityList = [];
      }
      home.value.activityListRowsExist = false;
    }

    // 초기 검색이면 컴포넌트에서 스크롤을 상단으로 이동하도록 플래그 설정
    // (Store에서 직접 스크롤 조작하지 않음 - 컴포넌트에서 처리)
  } catch (error) {
    console.error('Activity search failed:', error);
    if (isInitialSearch) {
      home.value.activityList = [];
    }
    home.value.activityListRowsExist = false;
  }
};

const handleActivitySearch = (isInitialSearch = true) =>
  runHomeSearchSerialized(homeActivitySearchMutex, () => handleActivitySearchImpl(isInitialSearch));

// 열려있는 카드 상세 모달 전체 닫기
const closeAllCardModals = () => {
  cardModals.value = [];
  floatingPopupCount.value = 0;
  fixedPopupCount.value = 0;
};

/** 열린 카드 모달 중 최고 z-index (없으면 1000) */
const getHighestCardModalZIndex = (excludeModalId?: number) =>
  cardModals.value
    .filter((m: any) => excludeModalId == null || m.id !== excludeModalId)
    .reduce((max, m: any) => Math.max(max, m.zIndex || 0), 1000);

const getNextCardModalZIndex = () => getHighestCardModalZIndex() + 10;

// 모달 활성화 (z-index만 올림 — cardModals 배열 splice/push로 순서 바꾸면 v-for DOM 재배치로 TinyMCE iframe이 깨질 수 있음)
const activateModal = (modalId: number) => {
  const modalIndex = cardModals.value.findIndex((modal: any) => modal.id === modalId);

  if (modalIndex >= 0 && cardModals.value[modalIndex].isFloating) {
    const modal = cardModals.value[modalIndex];
    modal.zIndex = getHighestCardModalZIndex(modalId) + 10;
  }
};

/** boardCategoryList → 협업/공유 카테고리 선택 옵션 동기화 */
const syncCoopPlanCategoryListFromBoard = () => {
  coopPlanCategoryList.value = [
    { value: '', label: '선택하세요' },
    ...boardCategoryList.value.map(board => ({
      value: board.categoryId,
      label: `[${board.boardNm}] ${board.categoryNm}`,
    })),
  ];
};

/** 전체 보드 카테고리 목록 서버 재조회 (카테고리 추가·삭제·이름변경 후) */
const handleRefreshBoardCategoryList = async () => {
  try {
    const res = await $api.fetchAllBoardCategoryList();
    boardCategoryList.value = res?.boardCategoryList || [];
    syncCoopPlanCategoryListFromBoard();
  } catch (e) {
    console.error('전체 보드 카테고리 목록 조회 실패:', e);
  }
};

/** 업무 모달 카테고리 선택용 — 전체 보드의 카테고리 목록 확보 */
const handleEnsureBoardCategoryList = async () => {
  if (boardCategoryList.value.length > 0) return;
  await handleRefreshBoardCategoryList();
};

/** 보드 DnD·업무 이동 후 열린 업무 모달의 계획 > 카테고리 동기화 */
const syncOpenCardModalsAfterSortOrder = (
  sortPayload: Array<{ categoryId: string; cardIds: Array<{ id: string }> }>,
) => {
  if (!sortPayload?.length || cardModals.value.length === 0) return;

  const findCategoryMeta = (categoryId: string) => {
    const fromBoard = boardCategoryList.value.find(c => String(c.categoryId) === String(categoryId));
    if (fromBoard) return fromBoard;
    const fromList = categoryList.value.find(c => String(c.categoryId) === String(categoryId));
    return fromList ? { ...fromList, boardNm: '' } : null;
  };

  sortPayload.forEach(({ categoryId, cardIds }) => {
    if (!categoryId || !cardIds?.length) return;
    const categoryMeta = findCategoryMeta(categoryId);

    cardIds.forEach(card => {
      const planId = card.id;
      if (!planId || planId === 'empty') return;

      const modal = cardModals.value.find((m: any) => String(m.card?.id) === String(planId));
      if (!modal?.planData) return;
      if (String(modal.planData.categoryId) === String(categoryId)) return;

      modal.planData.categoryId = categoryId;
      if (categoryMeta?.categoryNm) {
        modal.planData.categoryNm = categoryMeta.categoryNm;
      }
      modal.card.categoryId = categoryId;
      modal.refreshNonce = (modal.refreshNonce || 0) + 1;
    });
  });
};

/** 카테고리 일괄 협업/공유 변경 후 열린 업무 모달의 협업·공유자 동기화 */
const syncOpenCardModalsAfterCoopCategoryChange = async (categoryId: string) => {
  if (!categoryId || cardModals.value.length === 0) return;

  const targets = cardModals.value.filter((m: any) => {
    if (m.modalType === 'add' || !m.planData?.planId) return false;
    const modalCategoryId = m.planData.categoryId ?? m.card?.categoryId;
    return String(modalCategoryId) === String(categoryId);
  });

  if (targets.length === 0) return;

  for (const modal of targets) {
    const planId = modal.planData.planId || modal.card?.id;
    if (!planId) continue;

    try {
      const planDataResponse = await handleFetchPlan(String(planId));
      if (planDataResponse?.planData) {
        modal.planData = { ...planDataResponse.planData };
        modal.coopUserList = [...(planDataResponse.coopUserList || [])];
        modal.shareUserList = [...(planDataResponse.shareUserList || [])];
        modal.card.title = planDataResponse.planData.planNm || modal.card.title;
        modal.planAuthGbn = planDataResponse.authGbn || authGbn.value;
        if (planDataResponse.planData.categoryId) {
          modal.card.categoryId = planDataResponse.planData.categoryId;
        }
      }
      modal.modalType = modal.modalType || 'detail';
      modal.refreshNonce = (modal.refreshNonce || 0) + 1;
      await nextTick();
    } catch (e) {
      console.error('열린 업무 모달 협업/공유 동기화 실패:', e);
    }
  }
};

/** 카테고리 일괄 전달 등 — 해당 카테고리에 속한 열린 업무 모달 닫기 */
const closeCardModalsInCategory = (categoryId: string) => {
  if (!categoryId || cardModals.value.length === 0) return;

  const isModalInCategory = (m: any) => {
    const modalCategoryId = m.planData?.categoryId ?? m.card?.categoryId;
    return String(modalCategoryId) === String(categoryId);
  };

  const remaining = cardModals.value.filter((m: any) => !isModalInCategory(m));
  if (remaining.length === cardModals.value.length) return;

  cardModals.value = remaining;
  floatingPopupCount.value = cardModals.value.filter((m: any) => m.isFloating).length;
  fixedPopupCount.value = cardModals.value.filter((m: any) => !m.isFloating).length;
};

/** 열려 있는 업무카드 계획·활동 재조회 후 모달에 그대로 반영 */
const refreshOpenCardModals = async () => {
  for (const modal of cardModals.value) {
    if (modal.modalType === 'add') continue;
    const planId = modal.planData?.planId || modal.card?.id;
    if (!planId) continue;

    const planDataResponse = await handleFetchPlan(String(planId));
    const activityDataResponse = await handleFetchActivityList(planId);
    if (planDataResponse?.planData) {
      modal.planData = planDataResponse.planData;
      modal.coopUserList = planDataResponse.coopUserList || [];
      modal.shareUserList = planDataResponse.shareUserList || [];
      modal.card.title = planDataResponse.planData.planNm || modal.card.title;
      modal.planAuthGbn = planDataResponse.authGbn || authGbn.value;
      if (planDataResponse.planData.categoryId) {
        modal.card.categoryId = planDataResponse.planData.categoryId;
      }
    }
    modal.activityData = Array.isArray(activityDataResponse) ? [...activityDataResponse] : [];
    modal.refreshNonce = (modal.refreshNonce || 0) + 1;
  }
};

// 카드 상세 모달 열기
const openCardDetail = async (
  cardId: string,
  categoryId?: string,
  allowMultiple: boolean = false,
) => {
  // 업무 메뉴 미사용/미권한 시 avt API(selectPlan.do 등) 호출을 막는다 - kpi에서 체크
  const { hasAvtMenu } = useKpiFormState();
  if (!hasAvtMenu.value) return;

  // findYear가 비어있으면 현재 연도로 초기화 (외부 페이지에서 호출 시)
  if (!findYear.value) {
    findYear.value = String(new Date().getFullYear());
  }

  // 이미 열린 모달인지 확인
  const existingModalIndex = cardModals.value.findIndex((modal: any) => modal.card.id === cardId);
  let planDataResponse: any = {};
  let activityDataResponse: any = {};

  if (existingModalIndex >= 0) {
    // 이미 열린 모달: 최상단으로 올리고 서버에서 계획·활동 재조회 (같은 카드 재클릭 시 최신 반영)
    const modal = cardModals.value[existingModalIndex];
    const highestZIndex = getHighestCardModalZIndex(modal.id);
    if (modal.zIndex <= highestZIndex) {
      modal.zIndex = highestZIndex + 10;
    }
    planDataResponse = await handleFetchPlan(cardId);
    activityDataResponse = await handleFetchActivityList(cardId);
    if (planDataResponse?.planData) {
      modal.planData = planDataResponse.planData;
      modal.coopUserList = planDataResponse.coopUserList || [];
      modal.shareUserList = planDataResponse.shareUserList || [];
      modal.card.title = planDataResponse.planData.planNm || modal.card.title;
      modal.planAuthGbn = planDataResponse.authGbn || authGbn.value;
      modal.modalType = modal.modalType || 'detail';
      if (categoryId) {
        modal.card.categoryId = categoryId;
      } else if (planDataResponse.planData.categoryId) {
        modal.card.categoryId = planDataResponse.planData.categoryId;
      }
    }
    modal.activityData = Array.isArray(activityDataResponse) ? [...activityDataResponse] : [];
    modal.refreshNonce = (modal.refreshNonce || 0) + 1;
    return;
  } else {
    planDataResponse = await handleFetchPlan(cardId);
    activityDataResponse = await handleFetchActivityList(cardId);

    // 외부 페이지(KPI 등)에서 호출 시 필요한 데이터 초기화
    if (!allowMultiple && planDataResponse.planData) {
      // 협업 수락 업무는 "내가 옮겨놓은 내 보드(coopBoardId)" 기준으로 카테고리를 조회해야 함.
      // 내 업무는 coopBoardId가 비어 있어 기존 boardId(원 보드)로 fallback → 회귀 없음.
      const planBoardId = planDataResponse.planData.coopBoardId || planDataResponse.planData.boardId;
      // status.boardId가 비어있으면 plan 응답의 boardId로 설정
      if (!status.boardId && planBoardId) {
        status.boardId = planBoardId;
      }
      // userList 초기화 (비어있을 때만)
      if (userList.value.length === 0) {
        try {
          const userParams = new URLSearchParams();
          userParams.append('isIncludeDept', 'Y');
          const userData = await $commonApi.fetchUserAllList(userParams);
          userList.value = userData.list || [];
        } catch (e) {
          console.error('사용자 목록 조회 실패:', e);
        }
      }
      if (transferUserList.value.length === 0) {
        await handleInitTransferUserList();
      }
      // categoryList 초기화 (비어있거나 보드가 바뀐 경우 재조회)
      const targetBoardId = planBoardId || status.boardId;
      if (targetBoardId && (categoryList.value.length === 0 || categoryListBoardId.value !== targetBoardId)) {
        try {
          const catParams = { findBoardId: targetBoardId };
          const catResponse = await $api.fetchCategoryList(catParams);
          categoryList.value = catResponse.categoryList || [];
          categoryListBoardId.value = targetBoardId;
        } catch (e) {
          console.error('카테고리 목록 조회 실패:', e);
        }
      }
      await handleEnsureBoardCategoryList();
    }
  }

  // 다중 모달을 허용하지 않는 경우 (다른 페이지에서 호출)
  if (!allowMultiple) {
    // 기존 모달을 모두 닫고 새로 열기
    cardModals.value = [];
    floatingPopupCount.value = 0;
    fixedPopupCount.value = 0;
  } else {
    // TaskBoard에서 호출 - 기존 로직 유지
    // 플로팅 모달이 있는 경우
    if (floatingPopupCount.value > 0) {
      const fixedModalIndex = cardModals.value.findIndex((modal: any) => !modal.isFloating);
      if (fixedModalIndex >= 0) {
        cardModals.value.splice(fixedModalIndex, 1);
      }
    } else if (cardModals.value.length > 0) {
      // 플로팅 모달이 없는 경우 기존 모달 제거
      cardModals.value = [];
    }
  }

  // 카드 객체 생성
  const card = {
    id: cardId,
    title: planDataResponse.planData?.planNm || '',
    categoryId: categoryId || planDataResponse.planData?.categoryId,
  };

  // 새 모달 생성
  cardModals.value.push({
    id: Date.now(),
    card: card,
    isOpen: true,
    isFloating: allowMultiple, // allowMultiple이 true이면 플로팅, false이면 고정
    zIndex: getNextCardModalZIndex(),
    planData: planDataResponse.planData,
    coopUserList: planDataResponse.coopUserList || [],
    shareUserList: planDataResponse.shareUserList || [],
    activityData: activityDataResponse,
    modalType: 'detail',
    planAuthGbn: planDataResponse.authGbn || authGbn.value,
    refreshNonce: 0,
  });

  fixedPopupCount.value = 1;
  if (allowMultiple) {
    floatingPopupCount.value++;
  }
};

const isPdfDateModalOpen = ref(false);
const pdfExportDateRange = ref([null, null] as (string | null)[]);
const pdfExportPlanId = ref('');
const pdfExportPlanNm = ref('');
/** 활동내보내기 모달 탭: excel | pdf */
const activityExportTabId = ref('excel');

/** PDF/엑셀 다운로드용 날짜 → YYYYMMDD (값 없으면 빈값) */
const toPdfYyyymmdd = (value: unknown) => {
  if (!value) return '';
  return String(value).replace(/\D/g, '');
};

const closePdfDateModal = () => {
  isPdfDateModalOpen.value = false;
};

/** 활동내보내기 모달 오픈 (tabId: excel | pdf) */
const openActivityExportModal = (planId: string, planNm: string, tabId: 'excel' | 'pdf' = 'excel') => {
  if (isEmpty(planId)) {
    return;
  }

  pdfExportPlanId.value = planId;
  pdfExportPlanNm.value = planNm || '';
  pdfExportDateRange.value = [null, null];
  activityExportTabId.value = tabId === 'pdf' ? 'pdf' : 'excel';
  isPdfDateModalOpen.value = true;
};

// 활동 PDF 다운로드 — 내보내기 모달(PDF 탭) 오픈
const handleDownloadActivityPdf = (planId: string, planNm: string) => {
  openActivityExportModal(planId, planNm, 'pdf');
};

// 활동 엑셀 다운로드 — 내보내기 모달(엑셀 탭) 오픈
const handleDownloadActivityExcel = (planId: string, planNm: string) => {
  openActivityExportModal(planId, planNm, 'excel');
};

const handleConfirmPdfDownload = async () => {
  if (isEmpty(pdfExportPlanId.value)) {
    return;
  }

  const formData = {
    findYear: findYear.value,
    planId: pdfExportPlanId.value,
    planNm: pdfExportPlanNm.value || '',
    findFromDt: toPdfYyyymmdd(pdfExportDateRange.value?.[0]),
    findToDt: toPdfYyyymmdd(pdfExportDateRange.value?.[1]),
  };

  const actionUrl =
    activityExportTabId.value === 'excel'
      ? '/api/hcm/avt/avtMng/excelDownload.do'
      : '/api/hcm/avt/avtMng/pdfDownload.do';

  await downloadExcel({
    actionUrl,
    formData,
  });
  closePdfDateModal();
};

// 카드 삭제 함수
const deleteCardFromStore = async (cardId: string) => {
  const { showAlert, showConfirmDialog } = await import('~/composables/useConfirmDialog');

  const modal = cardModals.value.find(
    (m: any) => String(m.planData?.planId) === String(cardId) || String(m.card?.id) === String(cardId),
  );
  const planType = modal?.planData?.planType;

  const confirmConfig =
    planType === 'C'
      ? {
          title: appT('task.board.confirm.disconnectCoop.title'),
          message: appT('task.board.confirm.disconnectCoop.message'),
          success: appT('task.board.alert.disconnectCoopSuccess'),
        }
      : planType === 'S'
        ? {
            title: appT('task.board.confirm.disconnectShare.title'),
            message: appT('task.board.confirm.disconnectShare.message'),
            success: appT('task.board.alert.disconnectShareSuccess'),
          }
        : {
            title: appT('task.board.confirm.deleteTask.title'),
            message: appT('task.board.confirm.deleteTask.message'),
            success: appT('task.board.alert.deleteTaskSuccess'),
          };

  showConfirmDialog(confirmConfig.title, confirmConfig.message, async () => {
    try {
      const params = {
        findYear: findYear.value,
        findBoardId: status.boardId,
        boardId: status.boardId,
        planId: cardId,
      };

      const response = await $api.deletePlan(params);
      if (response.result === 'SUCCESS') {
        showAlert(appT('common.success'), confirmConfig.success);
        // 보드 리로드
        selectedBoard.value = null;
        await nextTick();
        selectedBoard.value = status.boardId;
        // 삭제한 카드 모달만 닫기
        cardModals.value = cardModals.value.filter(
          (m: any) =>
            String(m.planData?.planId) !== String(cardId) && String(m.card?.id) !== String(cardId),
        );
        floatingPopupCount.value = cardModals.value.filter((m: any) => m.isFloating).length;
        fixedPopupCount.value = cardModals.value.filter((m: any) => !m.isFloating).length;
      } else {
        showAlert(appT('common.error'), appT('task.board.alert.deleteTaskFailed'));
      }
    } catch (error) {
      showAlert(appT('common.error'), appT('task.board.alert.deleteTaskFailed'));
    }
  });
};

/** 홈 피드 활동/피드백 → 모달 data-activity-id 경로 (activityNo 또는 activityNo_replyNo) */
const buildFocusActivityPathFromHomeActivity = (activity: {
  activityNo?: string | number;
  replyNo?: string | number;
}) => {
  const activityNo = String(activity?.activityNo ?? '');
  if (!activityNo) return '';
  const replyNo = activity?.replyNo;
  if (replyNo != null && replyNo !== '' && String(replyNo) !== '0') {
    return `${activityNo}_${replyNo}`;
  }
  return activityNo;
};

/** mapActivityListForCards 결과에 포커스 대상 카드/피드백이 실제 존재하는지 확인 */
const isActivityPathInMappedItems = (items: any[], activityPath: string) => {
  if (!activityPath) return true;
  if (activityPath.includes('_')) {
    const [activityNo, replyNo] = activityPath.split('_');
    return items.some((item: any) => {
      if (String(item.id) !== activityNo) return false;
      const hasReply = (list: any[]) =>
        (list || []).some((r: any) => String(r.replyNo) === replyNo);
      if (hasReply(item.feedbackList)) return true;
      return (item.feedbackList || []).some((f: any) => hasReply(f.feedbackReplyList));
    });
  }
  return items.some((item: any) => String(item.id) === activityPath);
};

const isFocusActivityReady = (activityPath: string) => {
  const items = mapActivityListForCards(homeActivityListRaw.value);
  return isActivityPathInMappedItems(items, activityPath);
};

/**
 * 대상 활동/피드백이 포함될 때까지 홈 모달 활동 추가 로드
 * @param homeFeedBulkLoad — 홈 탭 활동/피드백 클릭 시 true (50건씩 페이징 로드, skipLoading, 완료 후 1회 렌더)
 */
const loadHomeActivitiesUntilFocus = async (
  planId: string,
  activityPath: string,
  planData?: { boardId?: string; boardCd?: string },
  homeFeedBulkLoad = false,
) => {
  if (!activityPath) return;

  if (isFocusActivityReady(activityPath)) {
    activities_home.value = mapActivityListForCards(homeActivityListRaw.value);
    return;
  }

  if (homeFeedBulkLoad) {
    homeActivityListRaw.value = [];
    homeActivityListRawPlanId.value = planId;

    const rows = HOME_MODAL_ACTIVITY_ROWS;
    let page = 1;
    let rawPageLength = rows;

    while (true) {
      const rawPage = await fetchHomeActivityListRaw(planId, page, planData, {
        rows,
        skipLoading: true,
      });
      rawPageLength = rawPage.length;
      homeActivityListRaw.value =
        page <= 1 ? rawPage : [...homeActivityListRaw.value, ...rawPage];
      homeModalActivityLoadedPage.value = page;
      homeModalActivityHasMore.value = rawPageLength >= rows;

      if (isFocusActivityReady(activityPath)) break;
      if (rawPageLength < rows) break;
      page++;
    }

    activities_home.value = mapActivityListForCards(homeActivityListRaw.value);
    return;
  }

  let page = homeModalActivityLoadedPage.value + 1;
  let rawPageLength = HOME_MODAL_ACTIVITY_ROWS;

  while (true) {
    const { items, rawPageLength: pageLen } = await handleFetchHomeActivityListAccumulated(
      planId,
      page,
      planData,
    );
    rawPageLength = pageLen;
    homeModalActivityLoadedPage.value = page;
    homeModalActivityHasMore.value = rawPageLength >= HOME_MODAL_ACTIVITY_ROWS;
    activities_home.value = items;

    if (isFocusActivityReady(activityPath)) {
      break;
    }
    if (rawPageLength < HOME_MODAL_ACTIVITY_ROWS) break;
    page++;
  }
};

type OpenCollaborationTaskModalOptions = {
  /** 홈 탭 포커스 bulk 로드 시 활동 50건 선조회 생략 */
  deferActivityLoad?: boolean;
  /** title NEW 버튼 표시 (기본 false) */
  showPlanReadNew?: boolean;
};

// CollaborationTaskModal 열기 함수
const openCollaborationTaskModal = async (
  planId: string,
  activityReadOnly?: boolean,
  feedbackReadOnly?: boolean,
  hideLogTab?: boolean,
  allowFloating?: boolean,
  showActivityEditDelete?: boolean,
  options?: OpenCollaborationTaskModalOptions,
) => {
  collabTaskModalActivityReadOnly.value = activityReadOnly === true;
  collabTaskModalFeedbackReadOnly.value = feedbackReadOnly === true;
  collabTaskModalHideLog.value = hideLogTab === true;
  collabTaskModalAllowFloating.value = allowFloating === true;
  collabTaskModalShowActivityEditDelete.value = showActivityEditDelete === true;
  collabTaskModalShowPlanReadNew.value = options?.showPlanReadNew === true;

  const switching = isTaskModalOpen.value && homeStatus.value.planId !== planId;
  if (switching) {
    isTaskModalSwitching.value = true;
  }

  homeStatus.value.planId = planId;

  try {
    const params = {
      findYear: findYear.value,
      findBoardId: homeStatus.value.boardId,
      findMon: '',
      findAnalCycle: '',
      planId: planId,
    };

    const planResponse = await $api.fetchPlan(params, {
      skipLoading: options?.deferActivityLoad === true,
    });
    if (homeStatus.value.planId !== planId) {
      return;
    }
    if (!planResponse.planData) {
      return;
    }

    if (planResponse.authGbn) {
      authGbn.value = planResponse.authGbn;
    }

    if (options?.deferActivityLoad) {
      homeActivityListRaw.value = [];
      homeActivityListRawPlanId.value = planId;
      homeModalActivityLoadedPage.value = 0;
      homeModalActivityHasMore.value = true;
      activities_home.value = [];
    } else {
      const { items: activityList, rawPageLength } = await handleFetchHomeActivityListAccumulated(
        planId,
        1,
        planResponse?.planData,
      );
      if (homeStatus.value.planId !== planId) {
        return;
      }

      homeModalActivityLoadedPage.value = 1;
      homeModalActivityHasMore.value = rawPageLength >= HOME_MODAL_ACTIVITY_ROWS;
      activities_home.value = activityList;
    }

    if (homeStatus.value.planId !== planId) {
      return;
    }
    avtLogList.value = [];
    taskModal.value = planResponse;
    isTaskModalOpen.value = true;
  } finally {
    isTaskModalSwitching.value = false;
  }
};

/** 홈 피드 바로가기 — 모달만 열고 첫 페이지(50건)만 로드 (포커싱/대상 활동 선로드 없음, 이후 무한스크롤) */
const openCollaborationTaskModalWithFocus = async (
  planId: string,
  activityPath?: string,
  activityReadOnly?: boolean,
  feedbackReadOnly?: boolean,
  hideLogTab?: boolean,
  allowFloating?: boolean,
  /** (미사용) 이전 bulk 로드 플래그 — 시그니처 호환용으로만 유지 */
  homeFeedBulkLoad = false,
  /** activityReadOnly일 때 수정/삭제 버튼 표시 (기본 false) */
  showActivityEditDelete = false,
  /** title NEW 버튼 표시 — 기본 false */
  showPlanReadNew = false,
) => {
  // 포커싱/대상 활동 선로드 없음 — 모달만 열고 첫 페이지(50건)만 로드, 나머지는 무한스크롤
  focusActivityScrollOnly.value = false;
  setTaskModalAccordionTab(TASK_MODAL_TAB.ACTIVITY);

  const openOptions: OpenCollaborationTaskModalOptions | undefined = showPlanReadNew
    ? { showPlanReadNew: true }
    : undefined;

  await openCollaborationTaskModal(
    planId,
    activityReadOnly,
    feedbackReadOnly,
    hideLogTab,
    allowFloating,
    showActivityEditDelete,
    openOptions,
  );
};

/**
 * Plan 검색 함수
 * Home 페이지에서 업무 목록을 조회
 * @param searchYn - 검색 여부 ('Y' 또는 'N')
 * @param clearActivityList - 활동 목록 초기화 여부 (기본값: true)
 */
const handlePlanSearchImpl = async (searchYn, clearActivityList = true) => {
  syncAppliedHomeBoardFromSelection();

  // clearActivityList가 true일 때만 모달 초기화 (활동 reload는 호출부 handleActivitySearch에서 처리)
  if (clearActivityList) {
    await homeTaskModalClear(false);
  }

  // 보드 미선택([]) — 서버는 빈 findBoardId를 전체 조회로 처리하므로 API 호출 전에 결과 초기화
  if (isHomeBoardUnselected()) {
    await clearHomeSearchResults(searchYn);
    return;
  }

  // 모달 동기화 리로드(searchYn 'N' + clearActivityList false) 시 업무 선택 상태 유지
  const preservePlanSelection = searchYn !== 'Y' && clearActivityList === false;
  const savedPlanSelection = preservePlanSelection
    ? new Map(
        filteredHomePlanList.map(t => [
          String(t.id),
          { selected: !!t.selected, showActivityList: !!t.showActivityList },
        ]),
      )
    : undefined;

  // 기존 데이터 초기화
  filteredHomePlanList.length = 0;
  home.value.planList = [];
  if (clearActivityList) {
    home.value.activityList = [];
  }

  // 보드 토글 상태 백업 (리로드 후 복원하기 위해)
  const savedBoardExpandedState = { ...boardExpandedState };

  // 페이지 초기화
  home.value.planListPage = 1;
  home.value.planListRowsExist = true;

  // 날짜 형식을 YYYYMMDD로 변환
  const formatDateToYYYYMMDD = dateStr => {
    if (!dateStr) return '';
    return dateStr.replace(/\./g, '');
  };

  const params = {
    findYear: findYear.value,
    findHomeYear: findHomeYear.value,
    findBoardId: getHomeBoardIdsForApi(),
    findHomeActivityStartDt: formatDateToYYYYMMDD(home.value.findHomeActivityStartDt),
    findHomeActivityEndDt: formatDateToYYYYMMDD(home.value.findHomeActivityEndDt),
    findPlanListGbnCd: 'home',
    findPlanNm: home.value.findPlanNm,
    searchDtCheck: 'Y',
    searchYn: searchYn || 'N',
    page: home.value.planListPage,
    rows: '9999',
  };
  const response = await $api.fetchPlanList(params);
  home.value.planList = response.planList;

  // 상단필터: planList를 기반으로 프론트엔드에서 생성
  home.value.subFilters = generateSubFilters(response.planList);

  // // 좌측필터
  // if (response.resultFilters) {
  //   home.value.resultFilters = response.resultFilters;
  // }

  // 복합필터
  if (response.combinedFilters) {
    home.value.combinedFilters = response.combinedFilters;
  }

  // 검색 버튼 클릭(searchYn === 'Y')일 때만 필터 상태 초기화
  if (searchYn === 'Y') {
    selectedSubFilterId.value = 'status-all'; // Header 상세 필터를 '전체'로 초기화
    selectedResultStatusCd.value = 'result-001'; // Index 왼쪽 메뉴를 '진행중'으로 초기화
  }

  // 더 가져올 데이터가 있는지 확인
  home.value.planListRowsExist = response.planList.length >= home.value.planListRowsPerPage;

  if (home.value.planList.length === 0) {
    filteredHomePlanList.length = 0;
    home.value.resultFilters = await generateResultFilters([]);
    return;
  }

  // home.value.activityList = response.activityList;
  homeStatus.value.boardId = getHomeBoardIdsForApi();

  // 서버에서 받은 데이터를 UI용 포맷으로 변환하여 filteredHomePlanList에 저장
  const newPlanList = home.value.planList.map((item: any) => ({
    id: item.planId,
    boardId: item.boardId,
    title: item.planNm,
    author:
      item.ownerYn == 'Y' ? '내 업무' : `${item.userNm} | ${getDeptNmByUserId(item.createUserId)}`,
    date: formatDate(item.endDt),
    endDt: item.endDt,
    statusColor: item.statusColor,
    statusText: item.resultStatusNm,
    selected: true,
    resultStatusCd: item.resultStatusCd || '005',
    resultStatusNm: getResultStatusName(item.resultStatusCd, item.resultStatusNm),
    activityCnt: item.activityCnt || 0,
    showActivityList: true,
    activityUnreadCnt: item.activityUnreadCnt || 0,
    newActivityCnt: item.newActivityCnt || 0,
    newFeedbackCnt: item.newFeedbackCnt || 0,
    ownerYn: item.ownerYn,
    planType: item.planType,
    importYn: item.importYn,
    statusCd: item.statusCd,
    planNm: item.planNm,
    createUserId: item.createUserId,
    userNm: item.userNm,
  }));
  filteredHomePlanList.splice(0, filteredHomePlanList.length, ...newPlanList);

  applyHeaderFilters(savedPlanSelection);
};

const handlePlanSearch = (searchYn, clearActivityList = true) =>
  runHomeSearchSerialized(homePlanSearchMutex, () =>
    handlePlanSearchImpl(searchYn, clearActivityList),
  );

/**
 * Plan 추가 로드 함수 (스크롤 페이징)
 * Home 페이지에서 스크롤 시 추가 데이터를 서버에서 가져옴
 */
const handleActivityLoadMore = async () => {
  // 더 가져올 데이터가 없으면 종료
  if (!home.value.planListRowsExist) {
    return false;
  }

  // 페이지 증가
  home.value.planListPage++;

  // 날짜 형식을 YYYYMMDD로 변환
  const formatDateToYYYYMMDD = dateStr => {
    if (!dateStr) return '';
    return dateStr.replace(/\./g, '');
  };

  const params = {
    findYear: findYear.value,
    findBoardId: selectedHomeBoardId.value,
    findHomeActivityStartDt: formatDateToYYYYMMDD(home.value.findHomeActivityStartDt),
    findHomeActivityEndDt: formatDateToYYYYMMDD(home.value.findHomeActivityEndDt),
    findPlanListGbnCd: 'home',
    findPlanNm: home.value.findPlanNm,
    searchDtCheck: 'Y',
    searchYn: 'N',
    page: home.value.planListPage,
    rows: home.value.planListRowsPerPage,
  };

  const response = await $api.fetchPlanList(params);

  // 기존 데이터에 추가
  if (response.planList && response.planList.length > 0) {
    home.value.planList = [...home.value.planList, ...response.planList];

    // 더 가져올 데이터가 있는지 확인
    home.value.planListRowsExist = response.planList.length >= home.value.planListRowsPerPage;

    // 서버에서 받은 추가 데이터를 UI용 포맷으로 변환하여 filteredHomePlanList에 추가
    const newItems = response.planList.map((item: any) => ({
      id: item.planId,
      boardId: item.boardId,
      title: item.planNm,
      author:
        item.ownerYn == 'Y'
          ? '내 업무'
          : `${item.userNm} | ${getDeptNmByUserId(item.createUserId)}`,
      date: formatDate(item.endDt),
      endDt: item.endDt,
      statusColor: item.statusColor,
      statusText: item.resultStatusNm,
      selected: true,
      resultStatusCd: item.resultStatusCd || '005',
      resultStatusNm: getResultStatusName(item.resultStatusCd, item.resultStatusNm),
      activityCnt: item.activityCnt || 0,
      showActivityList: true,
      activityUnreadCnt: item.activityUnreadCnt || 0,
      newActivityCnt: item.newActivityCnt || 0,
      newFeedbackCnt: item.newFeedbackCnt || 0,
      ownerYn: item.ownerYn,
      planType: item.planType,
      importYn: item.importYn,
      statusCd: item.statusCd,
      planNm: item.planNm,
      createUserId: item.createUserId,
      userNm: item.userNm,
    }));

    filteredHomePlanList.push(...newItems);

    applyHeaderFilters();

    return true;
  } else {
    home.value.planListRowsExist = false;
    return false;
  }
};

// 보드 기본 날짜 세팅
const handleDefaultBoardSearchDt = async () => {
  const defaultBoardSearchDt = await $api.fetchDefaultBoardSearchDt();

  // "null" 문자열이 들어올 경우 빈값으로 처리
  const fromDt = defaultBoardSearchDt.findFromDt === 'null' ? '' : defaultBoardSearchDt.findFromDt;
  const toDt = defaultBoardSearchDt.findToDt === 'null' ? '' : defaultBoardSearchDt.findToDt;

  const findDtRange = [fromDt, toDt];
  searchBox.value.findDtRange = findDtRange;

  if (isNotEmpty(fromDt)) {
    home.value.findFromDt = fromDt;
  }
  if (isNotEmpty(toDt)) {
    home.value.findToDt = toDt;
  }
};

// 에디터 빈글 체크
const isEditorEmpty = (html) => {
  if (!html) return true;
  /** Tinymce 빈 내용(<p><br></p> 등) 제외 — 실제 입력 여부 */
  const str = String(html);

  // 1. 시각적으로 의미 있는 태그들 (미디어 + 구조적 요소)이 있으면 내용이 있는 것으로 간주
  if (/<(img|iframe|video|audio|embed|object|svg|table|ul|ol|hr)\b/i.test(str)) {
    return false;
  }

  // 2. 위 태그들이 없을 때만 텍스트 유무를 검사
  const text = str.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim();
  return text.length === 0;
};

export const useCollaborationStore = () => {
  //type
  //api

  //변수
  //함수
  //initialize
  //modals
  //state = reactive();
  //ref(단일값)->.value, reactive(복합), watch deep..

  //const codeList = await codeStore.getCodeList('387');
  //useCommonStore

  return {
    findYear,
    findHomeYear,
    searchBox,
    home,
    board,
    plan,
    activity,
    coopPlanCnt,
    sharePlanCnt,
    deliverPlanCnt,
    hasSearchedPlanNm,
    boardRawCategoryCount,
    status,
    parts,
    settingData,
    dialog,
    homeDialog,
    homePlan,
    homeStatus,
    defaultParams,
    findStatusList,
    sortCategoryIds,
    planList,
    planList_home,
    boardList,
    boardListLoaded,
    categoriesLoaded,
    boardList_home,
    summaryBoardList,
    moveBoardId,
    moveBoardList,
    moveCategoryList,
    mergeBoardId,
    mergeBoardList,
    mergeCategoryList,
    mergePlanList,
    mailEventList,
    categoryList,
    categoryListBoardId,
    boardCategoryList,
    resultStatusList,
    avtPlanStatusList,
    findAvtPlanStatus,
    statusList,
    avtLogList,
    myShareUserList,
    coopPlanList,
    coopPlanCategoryList,
    userIdList,
    shareUserIdList,
    coopUserIdList,
    categoryUserIdList,
    categoryTakeoverUserIdList,
    planTakeoverUserIdList,
    activityLoadComplete,
    readGbn,
    isReadUserCntVisible,
    authGbn,
    homeActivityYn,
    summaryYn,
    summarySelectSelectedList,
    userList,
    transferUserList,
    handleInitTransferUserList,
    findTransferUserNm,
    findTransferUserLabel,
    formatTransferUserLabel,
    coopShareUserList,
    confirm,
    selectedBoard,
    selectedTaskList,
    isTaskModalOpen,
    taskModal,
    focusActivity,
    focusActivityScrollOnly,
    homeModalActivityLoadedPage,
    homeModalActivityHasMore,
    buildFocusActivityPathFromHomeActivity,
    loadHomeActivitiesUntilFocus,
    planNotifyRequest,
    activityNotifyRequest,
    activities_home,
    selectedHomeBoardId,
    appliedHomeBoardId,
    selectedBoardId,
    homeBoardOptionsList,
    boardExpandedState,
    sidebarScrollToPlanIdAfterSearch,
    resetBoardExpandedState,
    isHomePageReady,
    selectedSubFilterId,
    selectedResultStatusCd,
    filteredHomePlanList,
    // filteredHomeActivityList,
    isHome,
    isBoard,
    isFilterOpen,
    resultStatusCdOptions,
    statusCdOptions,
    cardModals,
    categories,
    isBoardAddModalOpen,
    openBoardAddModal,
    isFileAttachmentModalOpen_detailActivity,
    attachmentFiles_detailActivity,
    isFileAttachmentModalOpen_editActivity,
    attachmentFiles_editActivity,
    isFileAttachmentModalOpen_feedback,
    attachmentFiles_feedback,
    feedbackAttachmentApplyHandler,
    feedbackAttachmentCancelHandler,
    confirmFeedbackAttachmentModal,
    cancelFeedbackAttachmentModal,
    isFilterActive,
    isFilterActiveSearch,
    joinBoardSearchFilterValues,
    resetBoardSearchFilterState,
    handleClearBoardSearchTermDb,
    handleResetBoardSearchFilter,
    isInitialAllSelected,
    triggerActivitySearch,
    triggerPlanSearchReset,
    //taskResultStatusSummary,
    getTaskStatusColor,
    handleReadArticle,
    handleOpenDetailAttachment,
    homeTaskModalClear,
    openNewCardModal,
    initialize,
    resetHomeSearchConditions,
    floatingPopupCount,
    fixedPopupCount,
    openCardDetail,
    syncOpenCardModalsAfterSortOrder,
    syncOpenCardModalsAfterCoopCategoryChange,
    refreshOpenCardModals,
    closeCardModalsInCategory,
    closeAllCardModals,
    activateModal,
    handleFetchPlan,
    handleFetchActivityList,
    handleFetchActivityListAccumulated,
    handleFetchHomeActivityList,
    handleFetchHomeActivityListAccumulated,
    handleFetchAvtLogList,
    deleteCardFromStore,
    openCollaborationTaskModal,
    openCollaborationTaskModalWithFocus,
    applyHeaderFilters,
    refreshSubFilters,
    getDeptNmByUserId,
    iconStates,
    handlePlanSearch,
    handleActivityLoadMore,
    handleDefaultBoardSearchDt,
    isNewClick,
    collabTaskModalActivityReadOnly,
    collabTaskModalFeedbackReadOnly,
    collabTaskModalHideLog,
    collabTaskModalAllowFloating,
    collabTaskModalShowActivityEditDelete,
    collabTaskModalShowPlanReadNew,
    taskModalAccordionTab,
    isTaskModalSwitching,
    setTaskModalAccordionTab,
    applyTaskModalAccordionToMenuItems,
    getDefaultTaskModalMenuItems,
    handleActivitySearch,
    handleSaveHomeDefaultData,
    isSyncingHomeDefaultData,
    applyHomeDefaultDataResult,
    clearUnreadCountByCardId: clearUnreadCountByCardId, // <<<<<<<<<<<<<<<<<<<<<< 추가된 함수 export
    currentTask,
    handleEnsureBoardCategoryList,
    handleRefreshBoardCategoryList,
    handleDownloadActivityExcel,
    handleDownloadActivityPdf,
    isPdfDateModalOpen,
    pdfExportDateRange,
    pdfExportPlanId,
    activityExportTabId,
    closePdfDateModal,
    handleConfirmPdfDownload,
    isEditorEmpty
  };
};
